import React, { useState, useEffect, useContext } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Title, Tab, Tag, Field, Control, Button, Icon } from "rbx";
import { useQuery, useMutation } from "react-apollo";
import {
  Route,
  useParams,
  useLocation,
  useHistory,
  Switch
} from "react-router-dom";

import {
  SINGLE_EMPLOYEE_QUERY,
  UPDATE_EMPLOYEE_MUTATION,
  DELETE_EMPLOYEE_MUTATION
} from "../../../graphql/Employees/Employees.graphql";
import { ALL_SYSTEM_CODES_QUERY } from "../../../graphql/SystemCodes/SystemCodes.graphql";

import IconLink from "../../../components/IconLink";

import Payroll from "./routes/Payroll";
import Personal from "./routes/Personal";
import Vacation from "./routes/Vacation";
import Certifications from "./routes/Certifications";
import Notes from "./routes/Notes";

import {
  AddEmployeeCertificationForm,
  AddEmployeeNoteForm
} from "../../../components/Employees";

import { DeleteButton } from "../../../components/inputs";

import { ModalContext } from "../../../components/ModalProvider";

import { useIsDispatcher } from "../../../hooks";
import Authorization from "../../../components/Authorization";
import {
  getUTCFormattedDate,
  unmaskCurrency,
  formatCurrency,
  ourToast as toast
} from "../../../utils";
import Loading from "../../../components/Loading";

const PayrollAuth = Authorization(["ADMIN", "HR"]);

const INITIAL_STATE = {
  RecId: null,
  EmployeeId: null,
  LastName: "",
  FirstName: "",
  MiddleName: "",
  Active: true,
  Address1: null,
  Address2: null,
  City: null,
  State: null,
  ZipCode: null,
  Country: null,
  HomePhoneNo: null,
  CellPhoneNo: null,
  AlternatePhoneNo: null,
  EmergencyPhoneNo: null,
  EmergencyContact: null,
  EmergencyRelationship: null,
  Email1: null,
  Email2: null,
  Veteran: null,
  PayrollId: null,
  SocialSecurityNo: null,
  JobTitle: null,
  BirthDate: "",
  HireDate: "",
  TerminationDate: "",
  TerminationReasonRecIdRef: null,
  TerminationNote: null,
  RehireDate: "",
  PayrollNote: null,
  CompanyId: null,
  SupervisorRecIdRef: null,
  EthnicityRecIdRef: null,
  EmployeeTypeRecIdRef: null,
  ContactPreferenceRecIdRef: null,
  DriverFlag: null,
  MechanicFlag: null,
  DrugTest: null,
  HomeCompanyLocationId: null,
  ShopSteward: null,
  HourlyRate: "$0.00",
  DriverLicenseNo: null,
  DriverLicenseState: null,
  ELDStatusChange: false,
  PersonalDayStartDate: "",
  PersonalDaysRenewalDate: "",
  VacationDayStartDate: "",
  VacationDaysRenewalDate: "",
  SeniorityNo: ""
};

const EmployeePage = () => {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const [inputs, setInputs] = useState({ ...INITIAL_STATE });
  const [edited, setEdited] = useState(false);
  const [editing, setEditing] = useState(true);
  const { setModalOpen } = useContext(ModalContext);
  const { data: mechanicType } = useQuery(ALL_SYSTEM_CODES_QUERY, {
    variables: {
      where: {
        CodeDescription: "Mechanic",
        Category: "Employee",
        CodeName: "EmployeeType"
      },
      first: 1
    }
  });
  const { data: terminateType } = useQuery(ALL_SYSTEM_CODES_QUERY, {
    variables: {
      where: {
        CodeDescription: "Terminated",
        Category: "Employee",
        CodeName: "EmployeeType"
      },
      first: 1
    }
  });
  const EmployeeData = useQuery(SINGLE_EMPLOYEE_QUERY, {
    variables: { where: { RecId: parseInt(id) || null } }
  });

  const isDispatcher = useIsDispatcher();
  const [updateEmployee, { loading }] = useMutation(UPDATE_EMPLOYEE_MUTATION, {
    refetchQueries: ["SINGLE_EMPLOYEE_QUERY"]
  });

  useEffect(() => {
    if (
      EmployeeData?.data?.Employee &&
      !inputs.RecId &&
      !EmployeeData?.loading &&
      !loading
    ) {
      const info = EmployeeData.data.Employee;
      info.HourlyRate = info.HourlyRate
        ? formatCurrency(info.HourlyRate)
        : formatCurrency(0);
      setInputs(info);
    }
  }, [EmployeeData, inputs, loading]);

  const handleChange = e => {
    setEdited(true);
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    const updates = { ...inputs };
    updates[e.target.name] = value;

    if (e.target.name === "HourlyRate") {
      // If it's a currency input
      const unmaskedValue = unmaskCurrency(e.target.value);
      updates[e.target.name] = formatCurrency(unmaskedValue);
    } else if (e.target.name === "VacationDaysRenewalDate") {
      updates.PersonalDaysRenewalDate = value;
    } else if (e.target.name === "EmployeeTypeRecIdRef") {
      updates.DriverFlag = value === "30" || value === "34";
      updates.MechanicFlag = value === "32";
    }

    if (e.target.name === "DriverFlag") {
      updates.ELDStatusRecIdRef = value
        ? 314
        : EmployeeData?.data?.Employee?.ELDStatusRecIdRef;
    }
    const employeeTerminatedTypeId =
      parseInt(terminateType.SystemCodes[0].RecId, 10) || 35;

    if (
      e.target.name === "EmployeeTypeRecIdRef" &&
      parseInt(employeeTerminatedTypeId, 10) === parseInt(value, 10)
    ) {
      updates.Active = false;
    } else if (
      e.target.name === "EmployeeTypeRecIdRef" &&
      parseInt(employeeTerminatedTypeId, 10) !== parseInt(value, 10)
    ) {
      updates.Active = true;
    }
    if (
      EmployeeData?.data?.Employee?.DriverFlag &&
      (e.target.name === "FirstName" ||
        e.target.name === "MiddleName" ||
        e.target.name === "LastName" ||
        e.target.name === "DriverLicenseNo" ||
        e.target.name === "DriverLicenseState" ||
        e.target.name === "TerminationReasonRecIdRef")
    ) {
      updates.ELDStatusChange = true;
    }
    setInputs({ ...updates });
  };

  const handleSave = async goTo => {
    try {
      setEdited(false);
      if (
        location.pathname.includes("payroll") &&
        (!inputs.EmployeeTypeRecIdRef || inputs.EmployeeTypeRecIdRef === "0")
      ) {
        toast.error("Employee Type is required");
      } else {
        const obj = Object.keys(INITIAL_STATE).reduce(
          (acc, curr) =>
            inputs[curr] !== null ? { ...acc, [curr]: inputs[curr] } : acc,
          {}
        );

        if (String(inputs?.EmployeeTypeRecIdRef) !== "34") {
          obj.VacationDayStartDate = inputs.VacationDayStartDate
            ? getUTCFormattedDate(inputs.VacationDayStartDate)
            : null;
          obj.PersonalDayStartDate = inputs.PersonalDayStartDate
            ? getUTCFormattedDate(inputs.PersonalDayStartDate)
            : null;
          obj.VacationDaysRenewalDate = inputs.VacationDaysRenewalDate
            ? getUTCFormattedDate(inputs.VacationDaysRenewalDate)
            : null;
          obj.PersonalDaysRenewalDate = inputs.PersonalDaysRenewalDate
            ? getUTCFormattedDate(inputs.PersonalDaysRenewalDate)
            : null;
          obj.TerminationDate = inputs.TerminationDate
            ? getUTCFormattedDate(inputs.TerminationDate)
            : null;
          obj.TerminationReasonRecIdRef = parseInt(
            inputs.TerminationReasonRecIdRef
          );
          obj.VacationDaysAllowedThisYear = parseInt(
            inputs.VacationDaysAllowedThisYear
          );
          obj.VacationDaysUsedThisYear = parseInt(
            inputs.VacationDaysUsedThisYear
          );
          obj.PersonalDaysAllowedThisYear = parseInt(
            inputs.PersonalDaysAllowedThisYear
          );
          obj.PersonalDaysUsedThisYear = parseInt(
            inputs.PersonalDaysUsedThisYear
          );
          obj.Weight = parseInt(inputs.Weight);
        }
        obj.ELDStatusRecIdRef =
          inputs.ELDStatusRecIdRef && inputs.DriverFlag
            ? parseInt(inputs.ELDStatusRecIdRef)
            : null;
        obj.TerminationDate =
          inputs.TerminationDate !== "" ? inputs.TerminationDate : null;

        const data = {
          ...obj,
          HireDate: obj.HireDate ? getUTCFormattedDate(obj.HireDate) : null,
          BirthDate: obj.BirthDate ? getUTCFormattedDate(obj.BirthDate) : null,
          RehireDate: obj.RehireDate
            ? getUTCFormattedDate(obj.RehireDate)
            : null,
          SupervisorRecIdRef: parseInt(inputs?.SupervisorRecIdRef),
          EthnicityRecIdRef: parseInt(inputs?.EthnicityRecIdRef),
          EmployeeTypeRecIdRef: parseInt(inputs?.EmployeeTypeRecIdRef),
          ContactPreferenceRecIdRef: parseInt(
            inputs?.ContactPreferenceRecIdRef
          ),
          HourlyRate: parseFloat(unmaskCurrency(inputs?.HourlyRate)),
          TerminationReasonRecIdRef:
            inputs.TerminationReasonRecIdRef && obj.TerminationDate
              ? parseInt(inputs.TerminationReasonRecIdRef)
              : null,
          SeniorityDate: inputs.SeniorityDate
            ? getUTCFormattedDate(inputs.SeniorityDate)
            : null,
          LastWorkedDate: inputs.LastWorkedDate
            ? getUTCFormattedDate(inputs.LastWorkedDate)
            : null
        };

        await updateEmployee({
          variables: {
            data,
            where: { RecId: parseInt(id) }
          }
        });
        toast.success("Employee updated successfully.");
        if (typeof goTo === "string") {
          history.push(goTo);
        }
      }
    } catch (err) {
      console.log(err.message);
    }
  };
  const changeTab = tab => () => {
    if (
      edited &&
      (location.pathname.includes("personal") ||
        location.pathname.includes("payroll") ||
        location.pathname.includes("vacation"))
    ) {
      handleSave(`/hr/employees/${EmployeeData?.data?.Employee?.RecId}/${tab}`);
    } else {
      history.push(
        `/hr/employees/${EmployeeData?.data?.Employee?.RecId}/${tab}`
      );
    }
  };

  const toggleEditing = () => setEditing(!editing);
  if (loading || EmployeeData?.loading) return <Loading />;

  const getActionButtons = () => {
    if (location.pathname.includes("certifications")) {
      return (
        <Field kind="group">
          <Control>
            <Button
              color="success"
              disabled={isDispatcher}
              size="small"
              onClick={() =>
                setModalOpen(
                  true,
                  <AddEmployeeCertificationForm
                    EmployeeRecIdRef={parseInt(id)}
                    onComplete={() => setModalOpen(false, "")}
                  />
                )
              }
            >
              <Icon>
                <FontAwesomeIcon icon="plus" />
              </Icon>
              <span>Add Certification</span>
            </Button>
          </Control>
        </Field>
      );
    }
    if (location.pathname.includes("notes")) {
      return (
        <Field kind="group">
          <Control>
            <Button
              color="success"
              size="small"
              onClick={() =>
                setModalOpen(
                  true,
                  <AddEmployeeNoteForm
                    EmployeeRecIdRef={parseInt(id)}
                    onComplete={() => setModalOpen(false, "")}
                  />
                )
              }
            >
              <Icon>
                <FontAwesomeIcon icon="plus" />
              </Icon>
              <span>Add Note</span>
            </Button>
          </Control>
        </Field>
      );
    }
    return (
      <Field kind="group">
        <Control>
          <Button
            color="primary"
            disabled={editing}
            size="small"
            onClick={toggleEditing}
          >
            <Icon>
              <FontAwesomeIcon icon="edit" />
            </Icon>
            <span>Edit</span>
          </Button>
        </Control>
        {EmployeeData?.data?.Employee && (
          <React.Fragment>
            <Control>
              <DeleteButton
                confirmModalMessage="Are you sure do you want to delete this employee?"
                isDispatcher={isDispatcher}
                mutation={DELETE_EMPLOYEE_MUTATION}
                refetchQueries={[
                  "ALL_EMPLOYEES_QUERY",
                  "SINGLE_EMPLOYEE_QUERY"
                ]}
                size="small"
                variables={{ where: { RecId: parseInt(inputs.RecId) } }}
                onComplete={() => history.push(`/hr/employees`)}
              />
            </Control>
          </React.Fragment>
        )}
        <Control>
          <Button
            color="success"
            disabled={!editing || isDispatcher}
            size="small"
            onClick={handleSave}
          >
            <Icon>
              <FontAwesomeIcon icon="save" />
            </Icon>
            <span>Save</span>
          </Button>
        </Control>
      </Field>
    );
  };
  return (
    <React.Fragment>
      <header className="page-head">
        <div className="page-head-start">
          <Title color="primary" size={3}>
            {`${inputs?.FirstName} ${inputs?.LastName}`}
          </Title>
          <p>{EmployeeData?.data?.Employee?.EmployeeType?.CodeDescription}</p>
          <div className="page-head-start--subheader">
            <IconLink
              href={`mailto:${inputs?.Email1}`}
              icon="envelope"
              label={inputs?.Email1}
            />
            <IconLink
              href={`tel:${inputs?.HomePhoneNo}`}
              icon="phone-alt"
              label={inputs?.HomePhoneNo}
            />
          </div>
          {EmployeeData?.data?.Employee && (
            <Tag.Group gapless>
              <Tag color={inputs?.Active ? "success" : "danger"}>
                {inputs?.Active ? "ACTIVE" : "INACTIVE"}
              </Tag>
              {EmployeeData?.data?.Employee?.DriverFlag &&
                EmployeeData?.data?.Employee?.ELDStatus && (
                  <Tag color="dark">
                    {EmployeeData?.data.Employee.ELDStatus.CodeDescription}
                  </Tag>
                )}
            </Tag.Group>
          )}
        </div>
        <div className="page-head-end">{getActionButtons()}</div>
      </header>
      <Tab.Group kind="boxed">
        <Tab
          active={location.pathname.includes("personal")}
          as={Button}
          style={{ cursor: "pointer" }}
          onClick={changeTab("personal")}
        >
          <span>Personal</span>
        </Tab>
        {!isDispatcher && (
          <React.Fragment>
            <Tab
              active={location.pathname.includes("payroll")}
              as={Button}
              style={{ cursor: "pointer" }}
              onClick={changeTab("payroll")}
            >
              <span>Payroll</span>
            </Tab>
            <React.Fragment>
              {String(inputs?.EmployeeTypeRecIdRef) !== "34" && (
                <Tab
                  active={location.pathname.includes("vacation")}
                  as={Button}
                  style={{ cursor: "pointer" }}
                  onClick={changeTab("vacation")}
                >
                  <span>Vacation</span>
                </Tab>
              )}
            </React.Fragment>
          </React.Fragment>
        )}
        <Tab
          active={location.pathname.includes("certifications")}
          as={Button}
          style={{ cursor: "pointer" }}
          onClick={changeTab("certifications")}
        >
          <span>Certifications</span>
        </Tab>
        <Tab
          active={location.pathname.includes("notes")}
          as={Button}
          style={{ cursor: "pointer" }}
          onClick={changeTab("notes")}
        >
          <span>Notes</span>
        </Tab>
      </Tab.Group>
      <Switch>
        <Route path="/hr/employees/:id/personal">
          <Personal
            editing={editing && !isDispatcher}
            employee={inputs}
            mechanicTypeRecIdRef={mechanicType?.SystemCodes[0]?.RecId || 32}
            onChange={handleChange}
          />
        </Route>
        <Route path="/hr/employees/:id/payroll">
          {PayrollAuth(Payroll, {
            employee: inputs,
            onChange: handleChange,
            mechanicTypeRecIdRef: mechanicType?.SystemCodes[0]?.RecId || 32,
            editing
          })}
        </Route>
        <Route path="/hr/employees/:id/vacation">
          <Vacation
            editing={editing}
            employee={inputs}
            onChange={handleChange}
          />
        </Route>
        <Route path="/hr/employees/:id/certifications">
          <Certifications
            editing={editing && !isDispatcher}
            employeeRecId={inputs?.RecId}
            isDispatcher={isDispatcher}
            onChange={handleChange}
          />
        </Route>
        <Route path="/hr/employees/:id/notes">
          <Notes
            editing={editing}
            employeeRecId={inputs?.RecId}
            onChange={handleChange}
          />
        </Route>
      </Switch>
    </React.Fragment>
  );
};
EmployeePage.propTypes = {};

export default EmployeePage;
